import { OrderDirection } from "_shared/_api/ordering/order-direction";
import Guid from "_shared/guids/guid";
import AHDate from "_shared/dates/date";
import {
  ProposalBoxType,
  ProposalContext,
  ProposalEvent,
  ProposalMove,
  ProposalOrderField,
  ProposalOutcome,
  ProposalStage,
  ProposalStatus,
} from "work/proposals/__constants/proposal-lifecycle";
import Session from "users/__session/session";
import ProposalFilterSchema, {
  type ProposalFilterParametersProps,
} from "work/proposals/_api/queries/_schemas/proposal-filter-schema";
import type { ProposalOrderParametersProps } from "work/proposals/_api/queries/_schemas/proposal-order-schema";
import ProposalQuerySchema, {
  type ProposalQueryJSON,
  type ProposalQueryParams,
} from "work/proposals/_api/queries/_schemas/proposal-query-schema";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { Query } from "_shared/_api/queries/query";
import { Currency } from "_shared/money/currencies/currency";
import { WorkTeamSide } from "work/work-agreements/teams/leader-handover/_schemas/team-leader-handover-schema";
import WorkRecordCriteria from "work/work-records/work-record-criteria";
import { WorkTeamRole } from "work/work-records/work-team-role";

/** The Negotiable column filter's parameter before it took the name the API binds, `isNegotiable`. */
export const LEGACY_NEGOTIABLE_PARAMETER = "negotiable";

/** The list page's choice to show one row for each request sent to several firms (#6460). */
export const GROUP_BY_REQUEST_PARAMETER = "groupByRequest";

export default class ProposalQuery extends Query<
  ProposalFilterParameters,
  ProposalOrderParameters,
  PaginationParameters
> {
  public static fromJSON(json: ProposalQueryJSON): ProposalQuery {
    if (!json) return json;
    // Decoded, not validated as the domain shape: the store keeps ids and instants as strings, and
    // the domain shape refused the first id a list URL put there.
    const parsed = ProposalQuerySchema.deserialize(json);
    return new ProposalQuery(parsed);
  }

  public constructor(params: ProposalQueryParams) {
    super({
      filterParameters: new ProposalFilterParameters(params.filterParameters),
      orderParameters: new ProposalOrderParameters(params.orderParameters),
      paginationParameters: PaginationParameters.create(params.paginationParameters),
    });
  }

  public toJSON(): ProposalQueryJSON {
    return ProposalQuerySchema.serialize(this);
  }

  public isEqualTo(other: ProposalQuery): boolean {
    if (!other) return false;
    return (
      this.filterParameters.isEqualTo(other.filterParameters) &&
      this.orderParameters.isEqualTo(other.orderParameters) &&
      this.paginationParameters.isEqualTo(other.paginationParameters)
    );
  }
}

/**
 * The criteria a proposal list asks the API for, as `GET /work/proposals` binds them.
 *
 * Every criterion of `Work.Proposals.ProposalFilterParameters` the page can show is read back from
 * the URL (#6221), so a link the assistant writes from the criteria it counted with opens a list of
 * the same proposals. The names and spellings are `WorkRecordCriteria.PROPOSALS`, held to the
 * server's by `work-record-list-links.json`.
 */
export class ProposalFilterParameters {
  /**
   * The criteria a list page's URL sets, with what the page decides for itself: the side from the
   * session, and the stage, move, outcome and archived state from its chips (`proposalListFilter`).
   */
  public static fromSearchParams(
    searchParams: URLSearchParams,
    session: Session,
    list: {
      boxType?: ProposalBoxType;
      stage?: ProposalStage | ProposalStage[];
      move?: ProposalMove | ProposalMove[];
      outcome?: ProposalOutcome;
      isArchived?: boolean;
    } = {}
  ): ProposalFilterParameters {
    const criteria = WorkRecordCriteria.PROPOSALS.read(searchParams);
    // `negotiable` is what the Negotiable column's filter wrote before the column used the name the
    // API binds; a bookmark from then still says it.
    const legacyNegotiable = searchParams.get(LEGACY_NEGOTIABLE_PARAMETER);
    if (criteria.isNegotiable === undefined && legacyNegotiable !== null) {
      criteria.isNegotiable = legacyNegotiable.toLowerCase() === "true";
    }
    return new ProposalFilterParameters(
      ProposalFilterSchema.ProposalFilterZ.parse({
        ...criteria,
        context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
        boxType: list.boxType,
        stage: list.stage,
        move: list.move,
        outcome: list.outcome,
        isArchived: list.isArchived,
        // How the page shows the proposals, not which ones: off unless the reader turned it on.
        groupByRequest: searchParams.get(GROUP_BY_REQUEST_PARAMETER)?.toLowerCase() === "true" || undefined,
      })
    );
  }

  public readonly query?: string;
  public readonly RFPId?: Guid;
  public readonly name?: string;
  public readonly description?: string;

  public readonly clientUserId?: Guid;
  public readonly clientEntityId?: Guid;
  public readonly clientTeamMemberUserId?: Guid;
  public readonly clientTeamMemberEntityId?: Guid;

  public readonly hasStart?: boolean;
  public readonly minStart?: AHDate;
  public readonly maxStart?: AHDate;

  public readonly hasEnd?: boolean;
  public readonly minEnd?: AHDate;
  public readonly maxEnd?: AHDate;
  public readonly inProgressOn?: AHDate;

  public readonly vendorTeamRestricted?: boolean;
  /** An additional vendor, not the vendor team's leader; see `teamMemberUserId` for the leader. */
  public readonly vendorUserId?: Guid;
  public readonly vendorEntityId?: Guid;
  public readonly vendorTeamMemberId?: Guid;
  public readonly vendorTeamMemberEntityId?: Guid;
  public readonly vendorTeamMarketplaceId?: Guid;

  public readonly additionalVendorUserId?: Guid;
  public readonly additionalVendorEntityId?: Guid;

  /** A person with a seat on a team; `teamSide` and `teamRole` say which seat. */
  public readonly teamMemberUserId?: Guid;
  public readonly teamSide?: WorkTeamSide;
  public readonly teamRole?: WorkTeamRole;
  public readonly counterpartyEntityId?: Guid;

  public readonly feeScheduleCategoryId?: Guid;
  public readonly feeScheduleCategoryName?: string;
  public readonly feeScheduleCategoryDescription?: string;
  public readonly feeIsFixed?: boolean;
  public readonly feeIsHourly?: boolean;
  public readonly minFeeRateAmount?: number;
  public readonly maxFeeRateAmount?: number;
  public readonly feeRateCurrency?: Currency;
  public readonly feeAssignedMemberUserId?: Guid;

  public readonly teamTemplateId?: Guid;
  public readonly teamTemplateName?: string;
  public readonly feeScheduleTemplateId?: Guid;
  public readonly feeScheduleTemplateName?: string;

  public readonly minDiscount?: number;
  public readonly maxDiscount?: number;

  public readonly minCreated?: AHDate;
  public readonly maxCreated?: AHDate;

  public readonly isNegotiable?: boolean;

  public readonly hasResponseDueBy?: boolean;
  public readonly minResponseDueBy?: AHDate;
  public readonly maxResponseDueBy?: AHDate;

  public readonly minUpdated?: AHDate;
  public readonly maxUpdated?: AHDate;

  public readonly isSuperseded?: boolean;
  public readonly supersededByProposalId?: Guid;

  public readonly clientReviewerId?: Guid;
  public readonly vendorReviewerId?: Guid;

  public readonly status?: ProposalStatus;
  public readonly isArchived?: boolean;
  public readonly isActive?: boolean;

  public readonly event?: ProposalEvent;
  public readonly eventActorUserId?: Guid;
  public readonly minEventDate?: AHDate;
  public readonly maxEventDate?: AHDate;

  public readonly boxType?: ProposalBoxType;
  /** The stage chips, one or several of which widen the list (#6712); see `ProposalStage`. */
  public readonly stage?: ProposalStage | ProposalStage[];
  /** The move chips: only the proposals on which one of these is the reader's move (#6473, #6712). */
  public readonly move?: ProposalMove | ProposalMove[];
  /** The archive's outcome chip: only the archived proposals that ended this way (#6576). */
  public readonly outcome?: ProposalOutcome;
  public readonly context?: ProposalContext;

  public readonly isDeleted?: boolean;
  public readonly maxDeleted?: AHDate;
  public readonly minDeleted?: AHDate;

  public readonly userId?: Guid;

  /**
   * One row for each request the reader sent to several firms: the newest of its proposals. The
   * server groups before it pages, so the page and its total count requests (#6460).
   */
  public readonly groupByRequest?: boolean;

  /** The criteria as the API binds them: an id as its value, an instant as an ISO 8601 timestamp. */
  public get searchParams(): URLSearchParams {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(this)) {
      if (value instanceof AHDate) {
        params.append(key, value.toISOString());
      } else if (Array.isArray(value)) {
        // A chip field with several values is a repeated parameter, which the API reads as one `IN`.
        for (const each of value) params.append(key, String(each));
      } else if (value !== undefined) {
        params.append(key, value?.toString() ?? "null");
      }
    }
    return params;
  }

  public constructor(params: ProposalFilterParametersProps = {}) {
    Object.assign(this, params);
  }

  /**
   * Whether both ask for the same proposals.
   *
   * An id and an instant are objects, and two reads of the same URL make two of each, so they are
   * compared by value: compared by reference, the page would see a new query on every render and
   * ask for the list again.
   */
  public isEqualTo(other: ProposalFilterParameters): boolean {
    const keys = new Set([...Object.keys(this), ...Object.keys(other)]);
    return [...keys].every((key) => {
      const value = this[key as keyof ProposalFilterParameters];
      const otherValue = other[key as keyof ProposalFilterParameters];
      if (value instanceof Guid && otherValue instanceof Guid) return value.isEqualTo(otherValue);
      if (value instanceof AHDate && otherValue instanceof AHDate) return value.isEqualTo(otherValue);
      if (Array.isArray(value) && Array.isArray(otherValue)) return value.join() === otherValue.join();
      return value === otherValue;
    });
  }
}

export class ProposalOrderParameters {
  public static fromSearchParams(searchParams: URLSearchParams): ProposalOrderParameters {
    let orderBy = ProposalOrderField.Created;
    let orderDir = OrderDirection.Descending;

    const orderByParam = searchParams.get("orderBy");
    const orderDirParam = searchParams.get("orderDir");

    if (orderByParam) {
      const enumValue = Object.values(ProposalOrderField).find(
        (field) => field.toLowerCase() === orderByParam.toLowerCase()
      );
      if (enumValue) {
        orderBy = enumValue;
      }
    }

    if (orderDirParam) {
      const enumValue = Object.values(OrderDirection).find(
        (field) => field.toLowerCase() === orderDirParam.toLowerCase()
      );
      if (enumValue) {
        orderDir = enumValue;
      }
    }

    return new ProposalOrderParameters({ orderBy, orderDir });
  }

  public readonly orderBy: ProposalOrderField;
  public readonly orderDir: OrderDirection;

  public get searchParams(): URLSearchParams {
    const params = new URLSearchParams();
    params.append("orderBy", this.orderBy);
    params.append("orderDir", this.orderDir);
    return params;
  }

  constructor(params: ProposalOrderParametersProps) {
    this.orderBy = params.orderBy;
    this.orderDir = params.orderDir;
  }

  public isEqualTo(other: ProposalOrderParameters): boolean {
    if (!other) return false;
    return this.orderBy === other.orderBy && this.orderDir === other.orderDir;
  }
}
